import { ISolutionBundleItem } from '@gn-audio/core/api/compatibility-guide/models/solution-bundle-item';
import { fetchEcomSetting } from '@gn-audio/core/api/site-settings/fetch-ecom-setting';
import { IEcomSetting } from "@gn-audio/core/api/site-settings/models/ecom-setting";
import { ITranslationService } from 'src/Foundation/Translation/platform';

import { Filter, IFilter } from '../models';

import { ICustomizerFilterService } from '../services/customizer-filter.service';

import { HeroCustomizerController, IHeroCustomizerScope } from '../hero-customizer-spot/hero-customizer.controller';

interface ICgCustomizerScope extends IHeroCustomizerScope {
  activeFilter: IFilter;
  ecomEnabled: boolean;
  selectedBundles: ISolutionBundleItem[];

  filterActive(filter: IFilter): boolean;
  getSelectedBundleSummary(): string;
  onSkipAccessories(filter: IFilter): void;
  onBundleIncluded(bundle: ISolutionBundleItem): void;
  onBundleExcluded(bundle: ISolutionBundleItem): void;
  onBundleReset(): void;
  setActiveFilter(filter: IFilter, index: number): void;
}

/**
 * @deprecated Should be converted into component!
 */
export class CgCustomizerController extends HeroCustomizerController {
  public static $inject: ReadonlyArray<string> = [
    '$scope',
    '$location',
    '$q',
    'customizerFilterService',
    'translation',
  ];

  protected filterTemplateSuffix = 'cg-customizer-spot-filter-';

  constructor(
    protected $scope: ICgCustomizerScope,
    $location: ng.ILocationService,
    protected $q: ng.IQService,
    filterService: ICustomizerFilterService,
    private translation: ITranslationService,
  ) {
    super($scope, $location, $q, filterService);

    this.$q.when(fetchEcomSetting())
      .then((setting: IEcomSetting | null): void => {
        this.$scope.ecomEnabled = !!setting?.ecomEnabled;
      });

    this.$scope.selectedBundles = [];
    this.$scope.filterActive = (filter: IFilter): boolean => filter === this.$scope.activeFilter;
    const $watcher: () => void = this.$scope.$watch(
      'filters',
      (): void => {
        $watcher();
        if (!Array.isArray(this.$scope.filters)) {
          return;
        }

        this.$scope.filters.push(
          new Filter('accessories', 'accessories', this.filterTemplateSuffix, {
            filterable: false,
            title: this.translation.getSystem('CgCustomizerFilter'),
          }),
        );

        this.$scope.activeFilter = this.$scope.filters[0];
      },
    );

    const originalFilterChangeFn: (filter: IFilter) => void = this.$scope.filterChange;
    this.$scope.filterChange = (filter: IFilter): void => {
      const filterIndex: number = this.$scope.filters.indexOf(filter);
      if (filterIndex < this.$scope.filters.length) {
        this.$scope.activeFilter = this.$scope.filters[filterIndex + 1];
      }

      originalFilterChangeFn.call(this.$scope, filter);
    };

    this.$scope.setActiveFilter = (filter: IFilter): void => {
      if (filter === this.$scope.activeFilter) {
        return;
      }

      const filterIndex: number = this.$scope.filters.indexOf(filter);
      const activeFilterIndex: number = this.$scope.filters.indexOf(this.$scope.activeFilter);
      if (activeFilterIndex > -1 && filterIndex > activeFilterIndex) {
        return;
      }

      this.$scope.activeFilter = filter;
      for (let i: number = filterIndex; i < this.$scope.filters.length; i++) {
        const scopeFilter: IFilter = this.$scope.filters[i];
        scopeFilter.value = undefined;
      }

      this.emptyBundles();
      originalFilterChangeFn(filter);
    };

    this.$scope.getSelectedBundleSummary = (): string => {
      if (this.$scope.selectedBundles.length === 0) {
        return '';
      }

      const dictionaryKey: string =
        this.$scope.selectedBundles.length === 1 ? 'CgCustomizerPlusAccessory' : 'CgCustomizerPlusAccessories';
      const accessoryCount: number = this.$scope.selectedBundles
        .map((b: ISolutionBundleItem): number => b.bundle.length)
        .reduce((prev: number, current: number): number => prev + current);

      return (this.translation.getSystem(dictionaryKey) || '').replace('{count}', String(accessoryCount));
    };

    this.$scope.onBundleIncluded = (bundle: ISolutionBundleItem): void => {
      this.$scope.selectedBundles = this.makeBundleCopy();
      this.$scope.selectedBundles.push(bundle);
    };

    this.$scope.onBundleExcluded = (bundle: ISolutionBundleItem): void => {
      let bundleIndex = -1;
      for (let i = 0; i < this.$scope.selectedBundles.length; i++) {
        if (bundle.bundleHash !== this.$scope.selectedBundles[i].bundleHash) {
          continue;
        }

        bundleIndex = i;
        break;
      }

      if (bundleIndex > -1) {
        this.$scope.selectedBundles = this.makeBundleCopy();
        this.$scope.selectedBundles.splice(bundleIndex, 1);
      }
    };

    this.$scope.onBundleReset = (): void => {
      this.$scope.selectedBundles = [];
    };

    this.$scope.onSkipAccessories = (filter: IFilter): void => {
      this.emptyBundles();
      this.$scope.filterChange(filter);
    };
  }

  private makeBundleCopy(): ISolutionBundleItem[] {
    return JSON.parse(JSON.stringify(this.$scope.selectedBundles || [])) as ISolutionBundleItem[];
  }

  private emptyBundles(): void {
    this.$scope.selectedBundles = [];
  }
}
